import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Wrench,
  Trash2,
  Edit,
  MoreVertical,
  Image as ImageIcon,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { Badge } from "@/components/ui/badge";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageContainer } from "@/components/layout/PageContainer";
import DefaultEditor from "react-simple-wysiwyg";
import RecordNotFound from "@/core/components/RecordNotFound";

const RepairCategories = () => {
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  // Reset pagination when search text changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText]);

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    image: null,
    parent_id: null,
    level: 0,
    sort_order: 0,
    is_active: true,
    is_featured: false,
    is_menu: false,
    is_home: false,
  });

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchMediaDetails = async (mediaId) => {
    try {
      const res = await apiClient.get(`/media/${mediaId}`);
      if (res.data && res.data.data) {
        setFormData((prev) => ({
          ...prev,
          image: res.data.data.url,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch media details:", error);
      toast.error("Failed to fetch media details");
    }
  };

  const handleMediaSelect = (selectedId) => {
    setSelectedImage(selectedId);
    fetchMediaDetails(selectedId);
    setIsMediaLibraryOpen(false);
  };

  // Queries
  const { data: categoryData, isLoading: isCategoriesLoading } = useQuery({
    queryKey: [
      "repair-categories",
      pagination.pageIndex,
      pagination.pageSize,
      searchText,
    ],
    queryFn: async () => {
      const res = await apiClient.get(`/category_repair`, {
        params: {
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
    enabled: hasPermission("repair category read"),
  });

  const categories = categoryData?.data || [];
  const pageCount = categoryData?.meta?.last_page || 1;
  const totalCount = categoryData?.meta?.total || 0;

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const url = data.id ? `/category_repair/${data.id}` : `/category_repair`;
      const method = data.id ? "put" : "post";
      const res = await apiClient({ method, url, data });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repair-categories"] });
      toast.success("Repair category saved successfully");
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to save category");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/category_repair/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repair-categories"] });
      toast.success("Repair category deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete category");
    },
  });

  const resetForm = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      image: null,
      parent_id: null,
      level: 0,
      sort_order: 0,
      is_active: true,
      is_featured: false,
      is_menu: false,
      is_home: false,
    });
    setSelectedImage(null);
  };

  const handleSave = async () => {
    if (!formData.name || formData.name.trim() === "") {
      toast.error("Please fill in the Category Name");
      return;
    }
    saveMutation.mutate(formData);
  };

  const handleEdit = (category) => {
    setFormData({
      ...category,
      is_active: Boolean(category.is_active),
      is_featured: Boolean(category.is_featured),
      is_menu: Boolean(category.is_menu),
      is_home: Boolean(category.is_home),
      parent_id: category.parent_id || null,
      level: parseInt(category.level) || 0,
      sort_order: parseInt(category.sort_order) || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.image && (
            <img
              src={row.original.image}
              alt=""
              className="h-8 w-8 rounded-md object-cover border"
            />
          )}
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="text-muted-foreground line-clamp-1">
          {row.original.description || "-"}
        </span>
      ),
    },
    {
      accessorKey: "is_featured",
      header: "Featured",
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.is_featured ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? "success" : "destructive"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    ...(hasPermission("repair category update") ||
    hasPermission("repair category delete")
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {hasPermission("repair category update") && (
                    <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                      <Edit className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                  )}
                  {hasPermission("repair category delete") && (
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDelete(row.original.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
        ]
      : []),
  ];

  if (!hasPermission("repair category read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Repair Categories"
          subtitle="Manage your category of repairs"
        />
        <PageHeaderActions>
          {hasPermission("repair category create") && (
            <Button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Add Category
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search categories..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={categories}
          getRowId={(row) => row.id}
          isLoading={isCategoriesLoading}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total categories: {totalCount}
              </span>
            </div>
          }
        />
      </TableCard>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {formData.id ? "Edit" : "Add"} Repair Category
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Parent Category</Label>
              <ReactSelect
                options={[
                  { value: null, label: "None" },
                  ...categories
                    .filter((c) => c.id !== formData.id)
                    .map((c) => ({ value: c.id, label: c.name })),
                ]}
                value={
                  formData.parent_id
                    ? {
                        value: formData.parent_id,
                        label: categories.find(
                          (c) => c.id === formData.parent_id,
                        )?.name,
                      }
                    : { value: null, label: "None" }
                }
                onChange={(opt) =>
                  setFormData({ ...formData, parent_id: opt?.value })
                }
                placeholder="Select Parent Category"
              />
            </div>

            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                placeholder="Enter category name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <div className="border rounded-md overflow-hidden bg-background [&_.rsw-toolbar]:flex-wrap">
                <DefaultEditor
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category Image</Label>
              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsMediaLibraryOpen(true)}
                  className="w-full h-24 flex flex-col gap-2 border-dashed"
                >
                  {formData.image ? (
                    <div className="relative h-full aspect-square">
                      <img
                        src={formData.image}
                        alt=""
                        className="h-full w-full object-cover rounded"
                      />
                    </div>
                  ) : (
                    <>
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        Select Image
                      </span>
                    </>
                  )}
                </Button>
                {formData.image && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive h-auto p-0 hover:bg-transparent"
                    onClick={() => setFormData({ ...formData, image: null })}
                  >
                    Remove Image
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <ReactSelect
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Inactive" },
                  ]}
                  value={{
                    value: formData.is_active,
                    label: formData.is_active ? "Active" : "Inactive",
                  }}
                  onChange={(opt) =>
                    setFormData({ ...formData, is_active: opt.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Featured</Label>
                <ReactSelect
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                  value={{
                    value: formData.is_featured,
                    label: formData.is_featured ? "Yes" : "No",
                  }}
                  onChange={(opt) =>
                    setFormData({ ...formData, is_featured: opt.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Spinner size="sm" className="mr-2" />
              ) : null}
              Save
            </Button>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              repair category and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={false}
        selectedIds={selectedImage ? [selectedImage] : []}
      />
    </PageContainer>
  );
};

export default RepairCategories;
